/**
 * Admins Service
 * API calls for admin profile operations
 */

import { API_ENDPOINTS } from '@/constants/api';
import { apiClient, ApiResponse } from './apiClient';
import { DoctorDTO } from './doctors';

export interface AdminStats {
  counts: {
    doctors: number;
    patients: number;
    admins: number;
    pendingApprovals: number;
  };
  appointments: {
    total: number;
    scheduled: number;
    cancelled: number;
    completed: number;
    requested: number;
  };
  lastSystemUpdate: string;
}

export interface AdminDTO {
  id: string;
  userId: string;
  department?:
    | 'Operações'
    | 'Suporte'
    | 'Financeiro'
    | 'Recursos Humanos'
    | 'Outro';
  permissions?: string;
  isActive: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    email: string;
    name: string;
  };
}

export interface CreateAdminDTO {
  userId: string;
  department?:
    | 'Operações'
    | 'Suporte'
    | 'Financeiro'
    | 'Recursos Humanos'
    | 'Outro';
  permissions?: string;
  notes?: string;
}

export interface UpdateAdminDTO {
  department?:
    | 'Operações'
    | 'Suporte'
    | 'Financeiro'
    | 'Recursos Humanos'
    | 'Outro';
  permissions?: string;
  isActive?: boolean;
  notes?: string;
}

export interface AuditLogDTO {
  id: string;
  type: string;
  category: 'HISTORICAL' | 'OCCURRENCE';
  actorId?: string;
  actorRole?: string;
  targetId?: string;
  targetRole?: string;
  description: string;
  metadata?: any;
  status?: string;
  message?: string;
  assignedAdminId?: string;
  resolvedByAdminId?: string;
  resolvedAt?: string;
  resolutionNote?: string;
  createdAt: string;
  updatedAt: string;
  actor?: { id: string; name: string; email: string };
  target?: { id: string; name: string; email: string };
  assignedAdmin?: { id: string; name: string; email: string };
  resolvedByAdmin?: { id: string; name: string; email: string };
}

/**
 * Get global statistics for the admin dashboard
 */
export async function getAdminStats(): Promise<ApiResponse<AdminStats>> {
  return apiClient.get(API_ENDPOINTS.ADMIN_DASHBOARD.STATS);
}

/**
 * Get list of doctors pending approval
 */
export async function getPendingApprovals(): Promise<ApiResponse<DoctorDTO[]>> {
  return apiClient.get(API_ENDPOINTS.ADMIN_DASHBOARD.PENDING_APPROVALS);
}

/**
 * List all admins
 */
export async function listAdmins(): Promise<ApiResponse<AdminDTO[]>> {
  return apiClient.get(API_ENDPOINTS.ADMINS.LIST);
}

/**
 * Get admin by ID
 */
export async function getAdminById(id: string): Promise<ApiResponse<AdminDTO>> {
  return apiClient.get(API_ENDPOINTS.ADMINS.GET(id));
}

/**
 * Get admin by user ID
 */
export async function getAdminByUserId(
  userId: string,
): Promise<ApiResponse<AdminDTO>> {
  return apiClient.get(API_ENDPOINTS.ADMINS.GET_BY_USER(userId));
}

/**
 * Create admin profile
 */
export async function createAdmin(
  data: CreateAdminDTO,
): Promise<ApiResponse<AdminDTO>> {
  return apiClient.post(API_ENDPOINTS.ADMINS.CREATE, data);
}

/**
 * Update admin profile
 */
export async function updateAdmin(
  id: string,
  data: UpdateAdminDTO,
): Promise<ApiResponse<AdminDTO>> {
  return apiClient.put(API_ENDPOINTS.ADMINS.UPDATE(id), data);
}

/**
 * Suspend admin account
 */
export async function suspendAdmin(id: string): Promise<ApiResponse<AdminDTO>> {
  return apiClient.post(API_ENDPOINTS.ADMINS.SUSPEND(id));
}

/**
 * Reactivate admin account
 */
export async function reactivateAdmin(id: string): Promise<ApiResponse<AdminDTO>> {
  return apiClient.post(API_ENDPOINTS.ADMINS.REACTIVATE(id));
}


// ─────────────────────────────────────────────────────────────────────────────
// Admin Corporate Analytics Types & API Calls
// ─────────────────────────────────────────────────────────────────────────────

export interface AnalyticsFilter {
  startDate?: string;
  endDate?: string;
  type?: 'video' | 'home' | 'clinic';
  specialty?: string;
  status?: string;
  doctorId?: string;
  city?: string;
  state?: string;
}

export interface AnalyticsOverview {
  doctors: {
    total: number;
    approved: number;
    pending: number;
    inactive: number;
    newInPeriod: number;
    growthRate: number;
  };
  patients: {
    total: number;
    active: number;
    inactive: number;
    newInPeriod: number;
    growthRate: number;
  };
}

export interface AnalyticsAppointments {
  total: number;
  byStatus: {
    requested: number;
    scheduled: number;
    completed: number;
    cancelled: number;
    noShow: number;
    others: number;
  };
  byType: {
    telemedicine: number;
    home: number;
    clinic: number;
  };
  bySpecialty: { specialty: string; count: number }[];
  byDoctor: { doctorName: string; count: number }[];
  averages: {
    perDay: number;
    perDoctor: number;
  };
  peaks: {
    daysOfWeek: { day: number; count: number }[];
    hoursOfDay: { hour: number; count: number }[];
  };
  evolutionByType: {
    month: string;
    telemedicine: number;
    home: number;
    clinic: number;
  }[];
}

export interface AnalyticsFinancial {
  revenue: {
    total: number;
    pending: number;
    refunded: number;
  };
  ticketAverage: number;
  paidCount: number;
  byType: {
    telemedicine: number;
    home: number;
    clinic: number;
  };
  bySpecialty: { specialty: string; sum: number }[];
  byDoctor: { doctorName: string; sum: number }[];
  evolution: { date: string; amount: number }[];
  warnings: string[];
}

export interface AnalyticsDoctorItem {
  id: string;
  name: string;
  specialty: string;
  totalAppointments: number;
  completedAppointments: number;
  cancelledAppointments: number;
  cancellationRate: number;
  noShowRate: number;
  averageRating: number | null;
  complaintsCount: number;
  requiresAttention: boolean;
}

export interface AnalyticsDoctors {
  rankingByVolume: AnalyticsDoctorItem[];
  topRated: AnalyticsDoctorItem[];
  worstRated: AnalyticsDoctorItem[];
  attentionNeeded: AnalyticsDoctorItem[];
}

export interface AnalyticsReports {
  total: number;
  byStatus: {
    pending: number;
    approved: number;
    denied: number;
  };
  against: {
    doctors: number;
    patients: number;
  };
  topDoctors: { name: string; count: number }[];
  topPatients: { name: string; count: number }[];
  averageResolutionTimeHours: number;
  evolution: { date: string; count: number }[];
}

export interface AnalyticsOperations {
  doctorApprovals: number;
  averageApprovalTimeDays: number;
  pendingDocuments: number;
  doctorsWaitingValidation: number;
  conversion: {
    doctorRegisterToApproved: number;
    patientRegisterToAttended: number;
  };
  adminActions: {
    resolvedReports: number;
    reviewedDocuments: number;
    total: number;
  };
}

function buildQueryString(params?: Record<string, any>): string {
  if (!params) return '';
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });
  const str = searchParams.toString();
  return str ? `?${str}` : '';
}

/**
 * Get Platform Growth overview analytics
 */
export async function getAnalyticsOverview(filters?: AnalyticsFilter): Promise<ApiResponse<AnalyticsOverview>> {
  return apiClient.get(`${API_ENDPOINTS.ADMIN_ANALYTICS.OVERVIEW}${buildQueryString(filters)}`);
}

/**
 * Get Appointments analytics
 */
export async function getAnalyticsAppointments(filters?: AnalyticsFilter): Promise<ApiResponse<AnalyticsAppointments>> {
  return apiClient.get(`${API_ENDPOINTS.ADMIN_ANALYTICS.APPOINTMENTS}${buildQueryString(filters)}`);
}

/**
 * Get Financial analytics (estimations based on doctor prices)
 */
export async function getAnalyticsFinancial(filters?: AnalyticsFilter): Promise<ApiResponse<AnalyticsFinancial>> {
  return apiClient.get(`${API_ENDPOINTS.ADMIN_ANALYTICS.FINANCIAL}${buildQueryString(filters)}`);
}

/**
 * Get Doctors Performance analytics
 */
export async function getAnalyticsDoctors(filters?: AnalyticsFilter): Promise<ApiResponse<AnalyticsDoctors>> {
  return apiClient.get(`${API_ENDPOINTS.ADMIN_ANALYTICS.DOCTORS}${buildQueryString(filters)}`);
}

/**
 * Get Complaints and Reports analytics
 */
export async function getAnalyticsReports(filters?: AnalyticsFilter): Promise<ApiResponse<AnalyticsReports>> {
  return apiClient.get(`${API_ENDPOINTS.ADMIN_ANALYTICS.REPORTS}${buildQueryString(filters)}`);
}

/**
 * Get Operational Performance analytics
 */
export async function getAnalyticsOperations(filters?: AnalyticsFilter): Promise<ApiResponse<AnalyticsOperations>> {
  return apiClient.get(`${API_ENDPOINTS.ADMIN_ANALYTICS.OPERATIONS}${buildQueryString(filters)}`);
}



/**
 * Get audit logs and occurrences
 */
export async function getAuditLogs(params?: any): Promise<ApiResponse<{ items: AuditLogDTO[]; total: number }>> {
  return apiClient.get(`${API_ENDPOINTS.AUDIT.LOGS}${buildQueryString(params)}`);
}

/**
 * Process an occurrence (resolve/reject report)
 */
export async function processOccurrence(id: string, data: { status: string; resolutionNote?: string }): Promise<ApiResponse<AuditLogDTO>> {
  return apiClient.patch(API_ENDPOINTS.AUDIT.PROCESS_OCCURRENCE(id), data);
}

/**
 * Suspend any user account (Doctor or Patient)
 */
export async function suspendUser(userId: string, reason: string): Promise<ApiResponse<{ success: boolean }>> {
  return apiClient.post(API_ENDPOINTS.ADMIN_USERS.SUSPEND(userId), { reason });
}

/**
 * Reactivate any user account (Doctor or Patient)
 */
export async function reactivateUser(userId: string, reason: string): Promise<ApiResponse<{ success: boolean }>> {
  return apiClient.post(API_ENDPOINTS.ADMIN_USERS.REACTIVATE(userId), { reason });
}

